// WebSocket 연결 상태 타입
export type WebSocketConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'closed';

// 연결 상태 변경 리스너 타입
export type ConnectionStateListener = (state: WebSocketConnectionState, retryCount?: number) => void;

// Polling fallback 전 최대 재시도 횟수
export const MAX_RETRY_BEFORE_FALLBACK = 20;

/**
 * WebSocket 연결 상태 관리자 (싱글톤)
 *
 * 연결 상태를 추적하고, 상태 변경 시 구독자들에게 알림을 보냅니다.
 *
 * 상태 흐름:
 * - closed → connecting → connected (정상 연결)
 * - connected → reconnecting → connected (재연결 성공)
 * - reconnecting → failed (MAX_RETRY_BEFORE_FALLBACK 초과)
 * - failed → connected (복구 성공)
 */
class WebSocketConnectionManager {
  private static instance: WebSocketConnectionManager;
  private state: WebSocketConnectionState = 'closed';
  private retryCount = 0;
  private listeners: Set<ConnectionStateListener> = new Set();

  static getInstance(): WebSocketConnectionManager {
    if (!WebSocketConnectionManager.instance) {
      WebSocketConnectionManager.instance = new WebSocketConnectionManager();
    }
    return WebSocketConnectionManager.instance;
  }

  getState(): WebSocketConnectionState {
    return this.state;
  }

  getRetryCount(): number {
    return this.retryCount;
  }

  setState(state: WebSocketConnectionState, retryCount?: number): void {
    this.state = state;
    if (retryCount !== undefined) {
      this.retryCount = retryCount;
    }
    this.listeners.forEach((listener) => listener(state, this.retryCount));
  }

  resetRetryCount(): void {
    this.retryCount = 0;
  }

  subscribe(listener: ConnectionStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export const wsConnectionManager = WebSocketConnectionManager.getInstance();

// 연결 상태 훅을 위한 유틸리티 함수
export function getWebSocketConnectionState(): WebSocketConnectionState {
  return wsConnectionManager.getState();
}

export function subscribeToWebSocketState(listener: ConnectionStateListener): () => void {
  return wsConnectionManager.subscribe(listener);
}
